(() => {
 const initialize = (root) => {
  if (root.dataset.nav04Ready) return;
  root.dataset.nav04Ready = 'true';
  const header = root.closest('#header-component');
  const panels = [...root.querySelectorAll('[data-nav04-panel]')];
  const drawer = root.querySelector('[data-nav04-drawer]');
  const desktop = root.classList.contains('nav04--desktop');
  const mossHost = drawer?.querySelector('[data-nav04-moss]');
  const mobileMedia = matchMedia('(max-width: 989px)');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const mossAbort = new AbortController();
  let mossLayer;
  let mossFrame;
  let mossOpen = false;
  let mossWidth = 0;
  let mossHeight = 0;
  const stopMoss = () => {
   cancelAnimationFrame(mossFrame);
   if (mossLayer) cancelAnimationFrame(mossLayer.raf);
   mossOpen = false;
  };
  const syncMoss = () => {
   if (!mossHost) return;
   if (!drawer.open || !mobileMedia.matches) { stopMoss(); return; }
   const engine = window.Moss04Engine;
   if (!engine) return;
   const config = window.__moss04 || {};
   const width = mossHost.clientWidth;
   const height = mossHost.clientHeight;
   if (!width || !height) return;
   const entering = !mossOpen;
   const resized = width !== mossWidth || height !== mossHeight;
   if (!mossLayer) mossLayer = new engine.Layer(mossHost, {
    lut: engine.makeLUT(config.palette),
    size: config.size || 1,
    dir: 'up',
    profile: (position) => engine.ss(.05, 1, position) * .9
   });
   if (!entering && !resized) return;
   cancelAnimationFrame(mossLayer.raf);
   mossLayer.build();
   mossWidth = width;
   mossHeight = height;
   mossOpen = true;
   const strength = Math.min(1, config.grow ?? 1);
   if (reducedMotion.matches || config.animate === false || !entering) mossLayer.paint(strength);
   else { mossLayer.paint(0, 0); mossLayer.grow(strength, Math.min(config.dur || 3000, 4000)); }
  };
  const queueMoss = () => { cancelAnimationFrame(mossFrame); mossFrame = requestAnimationFrame(syncMoss); };
  reducedMotion.addEventListener('change', () => { mossOpen = false; queueMoss(); }, { signal: mossAbort.signal });
  window.addEventListener('load', queueMoss, { once: true, signal: mossAbort.signal });
  document.addEventListener('shopify:section:unload', (event) => {
   if (!event.target.contains(root)) return;
   if (drawer) drawer.open = false;
   syncScrollLock();
   syncBackground();
   stopMoss();
   mossLayer?.cv.remove();
   mossAbort.abort();
  }, { signal: mossAbort.signal });

  let closeTimer;
  const blocked = new Map();
  const visible = () => root.getClientRects().length > 0;
  const scrollStyles = new Map();
  const syncScrollLock = () => {
   const locked = drawer?.open && mobileMedia.matches;
   const targets = [document.documentElement, document.body, document.querySelector('.page-wrapper')].filter(Boolean);
   if (locked) {
    targets.forEach((element) => {
     if (scrollStyles.has(element)) return;
     scrollStyles.set(element, { value: element.style.getPropertyValue('overflow'), priority: element.style.getPropertyPriority('overflow') });
     element.style.setProperty('overflow', 'hidden', 'important');
    });
   } else {
    scrollStyles.forEach(({ value, priority }, element) => {
     if (value) element.style.setProperty('overflow', value, priority);
     else element.style.removeProperty('overflow');
    });
    scrollStyles.clear();
   }
  };
  document.addEventListener('touchmove', (event) => {
   if (drawer?.open && mobileMedia.matches && !event.target.closest('.nav04__drawer-body')) event.preventDefault();
  }, { passive: false, signal: mossAbort.signal });
  const syncBackground = () => {
   if (!drawer) return;
   const targets = [document.querySelector('#MainContent'), document.querySelector('footer'), ...header.querySelectorAll('.header-logo, search-button, header-actions, .dropdown-localization')].filter(Boolean);
   if (drawer.open) {
    targets.forEach((element) => { if (!blocked.has(element)) blocked.set(element, element.inert); element.inert = true; });
   } else {
    blocked.forEach((value, element) => { element.inert = value; });
    blocked.clear();
   }
  };
  const update = () => {
   if (!header) return;
   header.style.setProperty('--nav04-bottom', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
   const isOpen = [...header.querySelectorAll('[data-nav04-panel], [data-nav04-drawer]')].some((panel) => panel.open);
   header.toggleAttribute('data-nav04-open', isOpen);
   header.setAttribute('data-menu-open-04', String(isOpen));
   syncBackground();
   syncScrollLock();
   queueMoss();
   header.dispatchEvent(new CustomEvent('nav04:change', { bubbles: true, detail: { open: isOpen } }));
  };
  const close = () => { panels.forEach((panel) => { panel.open = false; }); update(); };
  const open = (panel) => {
   clearTimeout(closeTimer);
   panels.forEach((other) => { if (other !== panel) other.open = false; });
   panel.open = true;
   update();
  };
  panels.forEach((panel) => {
   panel.addEventListener('toggle', () => {
    panel.querySelector(':scope > summary').setAttribute('aria-expanded', String(panel.open));
    if (panel.open) panels.forEach((other) => { if (other !== panel) other.open = false; });
    update();
   });
   panel.querySelector(':scope > summary').setAttribute('aria-expanded', String(panel.open));
   if (desktop) {
    panel.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') open(panel); });
    panel.querySelector(':scope > summary').addEventListener('keydown', (event) => { if (event.key === 'ArrowDown') { event.preventDefault(); open(panel); panel.querySelector('a')?.focus(); } });
    panel.addEventListener('pointerleave', (event) => {
     if (event.pointerType === 'mouse' && !panel.contains(document.activeElement)) closeTimer = setTimeout(close, 150);
    });
    panel.addEventListener('focusout', () => { requestAnimationFrame(() => { if (!panel.contains(document.activeElement) && !panel.matches(':hover')) close(); }); });
   }
  });
  if (drawer) {
   drawer.addEventListener('change', (event) => {
    if (event.target.matches('[data-nav04-localization]')) event.target.form.requestSubmit();
   });
   drawer.querySelector(':scope > summary').setAttribute('aria-expanded', String(drawer.open));
   drawer.addEventListener('toggle', () => {
    drawer.querySelector(':scope > summary').setAttribute('aria-expanded', String(drawer.open));
    if (!drawer.open) panels.forEach((panel) => { panel.open = false; });
    update();
    if (drawer.open) drawer.querySelector('.nav04__navigation summary, .nav04__navigation a')?.focus();
   });
   drawer.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !drawer.open) return;
    const controls = [...drawer.querySelectorAll('a, summary, button, input, select')].filter((element) => element.getClientRects().length);
    const first = controls[0]; const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
   });
  }
  document.addEventListener('keydown', (event) => {
   if (event.key !== 'Escape' || !visible()) return;
   const active = panels.find((panel) => panel.open && panel.contains(document.activeElement)) || panels.find((panel) => panel.open);
   if (!active && !drawer?.open) return;
   if (active) { active.open = false; active.querySelector('summary').focus(); }
   else if (drawer?.open) { drawer.open = false; drawer.querySelector('summary').focus(); }
   else close();
   update();
  });
  document.addEventListener('pointerdown', (event) => {
   if (!root.contains(event.target)) { close(); if (drawer) drawer.open = false; update(); }
  });
  window.addEventListener('resize', () => {
   if (desktop && window.innerWidth < 990) close();
   if (drawer && window.innerWidth >= 990) { drawer.open = false; close(); }
   update();
  });
  update();
 };
 const initializeAll = () => document.querySelectorAll('[data-nav04]').forEach(initialize);
 if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
 else initializeAll();
 document.addEventListener('shopify:section:load', initializeAll);
})();


